import React, { ReactNode } from 'react';

import * as S from './styles';

interface IProps {
  /** Where the action goes. Renders an anchor — middle-click and open-in-new-tab work. */
  to?: string;
  /** For an action that is not a navigation. Renders a button. */
  onClick?: () => void;
  /** Names the action for the tooltip and for screen readers. Required. */
  label: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * One row action.
 *
 *   <RowAction to={`/panel/ticket/tickets/${id}`} label="Open ticket">
 *     <OpenIcon />
 *   </RowAction>
 *
 * Pass an **inline** `<svg>` rather than a `<ReactSVG>`. That is what the
 * design file draws, it inherits `currentColor` without the injection hook
 * AppTable's `RowActions` needs, and it spares a list twenty HTTP fetches for
 * twenty copies of one icon.
 *
 * The component is sound. A row action appearing not to navigate under
 * automation was chased for two sessions and is an artefact of the test
 * environment, not a defect here: instrumented, a click issued cold at the
 * icon's coordinates delivers **no DOM event at all** — no pointerdown, no
 * mousedown, nothing — and the same click at the same coordinates after a
 * separate hover navigates every time. Nothing can be wrong in a `<Link>`
 * that never receives an event. See FINDINGS for the full trace; do not
 * rebuild this component around it.
 */
const RowAction: React.FC<IProps> = ({ to, onClick, label, children, disabled, className }) => {
  if (to && !disabled) {
    return (
      <S.ActionLink to={to} title={label} aria-label={label} className={className}>
        {children}
      </S.ActionLink>
    );
  }

  return (
    <S.ActionButton
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className={className}
    >
      {children}
    </S.ActionButton>
  );
};

export default RowAction;
